import React from 'react'
import { Calendar, CalendarX } from 'lucide-react'
import { format } from 'date-fns'
import EventCard from './EventCard'
import type { CalendarEvent } from '@/hooks/useCalendarEvents'
import { formatDayKey } from '@/lib/accountTimezone'
import { useTranslation } from 'react-i18next'

interface EventListProps {
  events: CalendarEvent[]
  selectedDate: Date
  loading?: boolean
  onEventClick?: (event: CalendarEvent) => void
}

const EventList: React.FC<EventListProps> = ({
  events,
  selectedDate,
  loading = false,
  onEventClick
}) => {
  const { t } = useTranslation('agenda')
  // `selectedDate` is a calendar day picked on the grid, not an instant: running it through
  // a timezone would slide the header to the previous day whenever the browser is ahead of
  // the account zone. Only the locale applies here.
  const formattedDate = formatDayKey(format(selectedDate, 'yyyy-MM-dd'), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  if (loading) {
    return (
      <div className="bg-white border border-border rounded-[20px] p-5 h-full shadow-sm dark:bg-[#27272A] dark:border-[#3F3F464D] dark:shadow-none dark:rounded-xl">
        <div className="h-6 w-48 bg-card-muted rounded animate-pulse mb-4 dark:bg-neutral-800" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-card-muted rounded-2xl animate-pulse dark:bg-neutral-800 dark:rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-border rounded-[20px] p-5 h-full flex flex-col shadow-sm dark:bg-[#27272A] dark:border-[#3F3F464D] dark:shadow-none dark:rounded-xl">
      <div className="flex items-center gap-2 mb-5">
        <Calendar className="w-5 h-5 text-[#2563EB] dark:text-[#60A5FA]" />
        <h3 className="text-lg font-semibold text-[#0F172A] capitalize dark:text-neutral-50">
          {formattedDate}
        </h3>
      </div>

      {events.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
          <div className="w-16 h-16 bg-[#DBEAFE] rounded-full flex items-center justify-center mb-4 dark:bg-blue-500/20">
            <CalendarX className="w-8 h-8 text-[#2563EB] dark:text-[#60A5FA]" />
          </div>
          <p className="text-[#64748B] text-sm dark:text-neutral-400">
            {t('noEvents')}
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {events.map(event => (
            <EventCard
              key={event.id}
              event={event}
              onClick={() => onEventClick?.(event)}
            />
          ))}
        </div>
      )}

      {events.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border dark:border-neutral-800">
          <p className="text-sm text-[#64748B] dark:text-neutral-500">
            {t('eventCount', { count: events.length })}
          </p>
        </div>
      )}
    </div>
  )
}

export default EventList
